/**
 * Hands-free voice capture with VAD (Voice Activity Detection).
 * After "Hey Buddy" the user should NOT hold any button: we open the mic,
 * wait for speech to start (RMS energy above an adaptive noise floor),
 * record while they talk, and stop automatically after ~0.8s of silence.
 * The captured audio blob is then sent to Whisper for accurate transcription.
 *
 * captureUtterance() returns { promise, cancel }:
 *   promise -> { blob, filename } when an utterance was captured,
 *              or null (silence timeout / cancelled / too short).
 *   cancel  -> aborts the session immediately (mic released).
 */
import { warmMic } from "./mic";

// ---------- tiny audio chime so the user knows Buddy started/stopped listening ----------
let chimeCtx = null;
const getChimeCtx = () => {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!chimeCtx) chimeCtx = new AC();
  if (chimeCtx.state === "suspended") chimeCtx.resume().catch(() => {});
  return chimeCtx;
};

export function chime(kind = "start") {
  try {
    const ctx = getChimeCtx();
    if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    if (kind === "start") {
      osc.frequency.setValueAtTime(620, now);
      osc.frequency.exponentialRampToValueAtTime(980, now + 0.12);
    } else {
      osc.frequency.setValueAtTime(880, now);
      osc.frequency.exponentialRampToValueAtTime(520, now + 0.12);
    }
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.1, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(now);
    osc.stop(now + 0.18);
  } catch (e) { /* chimes are best-effort */ }
}

// One shared AudioContext: creating/closing one per utterance costs 100-400 ms on iOS —
// time during which the user is already talking.
let sharedCtx = null;
function getCtx() {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!sharedCtx || sharedCtx.state === "closed") sharedCtx = new AC();
  return sharedCtx;
}

/** Recorder mime: Apple's WKWebView claims webm support but yields EMPTY blobs — prefer mp4 there. */
export function pickUtteranceMime() {
  const ua = navigator.userAgent || "";
  const apple = /iPhone|iPad|iPod|Macintosh/.test(ua) && !/Chrome|CriOS|Android/.test(ua);
  const order = apple
    ? ["audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/aac", "audio/webm;codecs=opus", "audio/webm"]
    : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
  for (const m of order) { try { if (window.MediaRecorder?.isTypeSupported?.(m)) return m; } catch (e) { /* ignore */ } }
  return "";
}

export function captureUtterance({
  waitForSpeechMs = 9000,   // give up if the user never starts talking
  silenceMs = 1000,         // pause length that means "I'm done talking"
  maxUtteranceMs = 25000,   // hard cap per utterance
  minSpeechMs = 250,        // ignore clicks/pops shorter than this
  armDelayMs = 0,           // grace period: ignore the tail of the wake word itself
  onSpeechStart = null,     // fired once when voice is first detected
  onReady = null,           // fired when the recorder is REALLY capturing (turn the red light on here)
} = {}) {
  let cancelled = false;
  let stopLoop = null;

  const cancel = () => {
    cancelled = true;
    try { stopLoop?.(); } catch (e) { /* ignore */ }
  };

  const promise = (async () => {
    let ctx = null;
    let recorder = null;
    let srcNode = null;
    try {
      let stream;
      try { stream = await warmMic(); } catch (e) {
        // first getUserMedia on a phone can fail while the audio session is being
        // reconfigured (wake recognizer just stopped) — one retry saves the utterance
        await new Promise((r) => setTimeout(r, 500));
        stream = await warmMic();
      }
      ctx = getCtx();
      if (ctx.state === "suspended") { try { await ctx.resume(); } catch (e) { /* ignore */ } }
      srcNode = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      srcNode.connect(analyser);

      const mime = pickUtteranceMime();
      recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      const chunks = [];
      recorder.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      recorder.start(150);
      try { onReady?.(); } catch (e) { /* ignore */ }

      const data = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      let speechAt = 0;
      let lastVoice = 0;
      let voicedMs = 0;
      let lastTick = Date.now();
      let noiseFloor = 0.006;

      const outcome = await new Promise((resolve) => {
        const iv = setInterval(() => {
          if (cancelled) { resolve("cancelled"); return; }
          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);
          const now = Date.now();
          const dt = now - lastTick;
          lastTick = now;

          if (!speechAt) {
            // adapt the noise floor slowly while waiting (fans, road noise...)
            noiseFloor = noiseFloor * 0.96 + Math.min(rms, 0.03) * 0.04;
            const startThresh = Math.max(noiseFloor * 3, 0.017);
            if (now - startedAt < armDelayMs) {
              // grace period — the tail of "Hey Buddy" must not count as the command
            } else if (rms > startThresh) {
              speechAt = now;
              lastVoice = now;
              voicedMs = dt;
              try { onSpeechStart?.(); } catch (e) { /* ignore */ }
            } else if (now - startedAt > waitForSpeechMs) {
              resolve("timeout");
            }
          } else {
            const keepThresh = Math.max(noiseFloor * 2.2, 0.012);
            if (rms > keepThresh) { lastVoice = now; voicedMs += dt; }
            if (now - lastVoice > silenceMs) resolve("done");
            else if (now - speechAt > maxUtteranceMs) resolve("done");
          }
        }, 60);
        stopLoop = () => { clearInterval(iv); resolve(cancelled ? "cancelled" : "stopped"); };
      });

      try { stopLoop?.(); } catch (e) { /* ignore */ }
      const stopped = new Promise((res) => {
        recorder.onstop = res;
        setTimeout(res, 800); // safety
      });
      try { recorder.stop(); } catch (e) { /* ignore */ }
      await stopped;
      try { srcNode.disconnect(); } catch (e) { /* ignore */ }
      ctx = null; // shared context stays alive for the next utterance

      if (outcome !== "done" || cancelled || voicedMs < minSpeechMs) return null;
      const type = recorder.mimeType || mime || "audio/webm";
      const blob = new Blob(chunks, { type });
      if (!blob.size) return null;
      return { blob, filename: type.includes("mp4") ? "buddy.m4a" : "buddy.webm" };
    } catch (e) {
      try { recorder?.stop(); } catch (err) { /* ignore */ }
      try { srcNode?.disconnect(); } catch (err) { /* ignore */ }
      return null;
    }
  })();

  return { promise, cancel };
}
